import { RedoButton } from "@redotech/arbiter/buttons/redo-button";
import { Flex } from "@redotech/arbiter/flex";
import {
  RedoInputState,
  sizeToDescriptorTextProps,
} from "@redotech/arbiter/input/base-redo-text-input";
import { RedoTextInput } from "@redotech/arbiter/input/redo-text-input";
import type { TextRedoListItem } from "@redotech/arbiter/list/redo-list";
import { RedoListDropdown } from "@redotech/arbiter/list/redo-list-dropdown";
import { Text } from "@redotech/arbiter/text";
import { ThirdPartyRefundProvider } from "@redotech/congo-enums/third-party-refund-provider";
import { countries } from "@redotech/locale/countries";
import { Currency } from "@redotech/money/currencies";
import { getCurrencyFormatter } from "@redotech/money/util";
import type { BankAccountOnboarding } from "@redotech/redo-customer-portal-shared/hooks/useRefundDestination/use-refund-destination";
import type { RefundDestinationForm } from "@redotech/redo-customer-portal-shared/hooks/useRefundDestination/use-refund-destination-form";
import { useTranslation } from "@redotech/redo-i18n";
import BankIcon from "@redotech/redo-icons/bank.svg";
import ChevronDownIcon from "@redotech/redo-icons/chevron-down_filled.svg";
import PaypalIcon from "@redotech/redo-icons/paypal-color.svg";
import VenmoIcon from "@redotech/redo-icons/venmo-color.svg";
import {
  CustomerRefundProvider,
  RefundRecipientField,
  thirdPartyRefundProviderName,
} from "@redotech/redo-return-core-common/third-party-refunds/recipient";
import { STRIPE_REFUND_RECIPIENT_COUNTRIES } from "@redotech/redo-return-core-common/third-party-refunds/stripe-recipient-countries";
import { memo, ReactNode, useMemo, useState } from "react";
import { PortalSelectionCard } from "./portal-selection-card";

export const ORIGINAL_PAYMENT = "original" as const;

const stripeCountrySet = new Set(STRIPE_REFUND_RECIPIENT_COUNTRIES);

/** ISO 3166-1 alpha-2 to its regional-indicator flag emoji. */
function countryFlag(code: string): string {
  return [...code]
    .map((letter) =>
      String.fromCodePoint(0x1_f1_e6 + letter.charCodeAt(0) - 65),
    )
    .join("");
}

/** US first (the default), then alphabetical. */
const bankCountryOptions: TextRedoListItem<string>[] = countries
  .filter((country) => stripeCountrySet.has(country.code))
  .sort((a, b) =>
    a.code === "US" ? -1 : b.code === "US" ? 1 : a.name.localeCompare(b.name),
  )
  .map((country) => ({
    id: country.code,
    type: "text" as const,
    text: `${countryFlag(country.code)} ${country.name}`,
    value: country.code,
  }));

const INPUT_SIZE = "sm" as const;

export interface RefundDestinationOptionsProps {
  form: RefundDestinationForm;
  /** Refund total in `currency`; drives the per-option description. */
  refundAmount: number;
  currency: Currency;
  /** Whether refunding to the original payment method is still on the table. */
  allowOriginalPayment: boolean;
  /** Shown next to the recipient field while a save is in flight. */
  saving?: boolean;
  errorMessage?: string;
  onRecipientBlur?: () => void;
  /** Drives the Stripe bank option; omit where a bank account can't be onboarded (the post-submission editor). */
  bankAccount?: BankAccountOnboarding;
}

/**
 * The rail picker itself, with the recipient field nested under the selected
 * option. Shared by the submission review screen and the post-submission
 * editor so the two can't drift apart visually or behaviourally.
 */
export const RefundDestinationOptions = memo(function RefundDestinationOptions({
  form,
  refundAmount,
  currency,
  allowOriginalPayment,
  saving = false,
  errorMessage,
  onRecipientBlur,
  bankAccount,
}: RefundDestinationOptionsProps) {
  const { t, i18n } = useTranslation();
  const [countrySearch, setCountrySearch] = useState("");
  const [countryOpen, setCountryOpen] = useState(false);
  const [countryAnchor, setCountryAnchor] = useState<HTMLButtonElement | null>(
    null,
  );
  const filteredCountryOptions = useMemo(() => {
    const query = countrySearch.trim().toLowerCase();
    return query
      ? bankCountryOptions.filter((option) =>
          option.text.toLowerCase().includes(query),
        )
      : bankCountryOptions;
  }, [countrySearch]);
  const {
    providers,
    selectedProvider,
    selectProvider,
    recipientField,
    recipientValue,
    setRecipientValue,
    markTouched,
    orderMismatchWarning,
  } = form;

  const amount = getCurrencyFormatter(
    currency,
    undefined,
    i18n.language,
  ).format(refundAmount);

  const isPhone = recipientField === RefundRecipientField.PHONE;
  const hasError = !!errorMessage;
  const inputState: RedoInputState = hasError ? "error" : "default";
  // Matches the size the input uses for its own label/error text.
  const descriptorTextSize = sizeToDescriptorTextProps[INPUT_SIZE];

  const recipientInput = (
    // Editing shouldn't register as a click on the card wrapping the field.
    <Flex
      dir="column"
      gap="xs"
      onClick={(event) => event.stopPropagation()}
      pt="lg"
      w="full"
    >
      <RedoTextInput
        ariaInvalid={hasError}
        autoSave={{ isSaving: saving }}
        dataTestId="refund-destination-recipient"
        errorText={errorMessage}
        fullWidth
        inputMode={isPhone ? "tel" : "email"}
        inputTypeHint={isPhone ? "tel" : "email"}
        label={isPhone ? t("Venmo mobile number") : t("PayPal email")}
        onBlur={onRecipientBlur ?? markTouched}
        placeholder={isPhone ? t("(555) 555-5555") : t("you@example.com")}
        setValue={setRecipientValue}
        size={INPUT_SIZE}
        state={inputState}
        value={recipientValue}
      />
      {!errorMessage && !!orderMismatchWarning && (
        <Text as="p" fontSize={descriptorTextSize} textColor="warning">
          {orderMismatchWarning}
        </Text>
      )}
    </Flex>
  );

  const bankAccountInput = bankAccount && (
    <Flex
      dir="column"
      gap="sm"
      onClick={(event) => event.stopPropagation()}
      pt="lg"
      w="full"
    >
      {bankAccount.state?.ready ? (
        <Text as="p" fontSize={descriptorTextSize} textColor="success">
          {t("Bank account connected")}
        </Text>
      ) : (
        <Text as="p" fontSize={descriptorTextSize} textColor="secondary">
          {t(
            "You'll add your bank details on Stripe's secure page, then come back here to submit.",
          )}
        </Text>
      )}
      {!bankAccount.state?.ready && bankAccount.state?.requirementsSummary && (
        <Text as="p" fontSize={descriptorTextSize} textColor="warning">
          {t("Stripe still needs: {{requirements}}", {
            requirements: bankAccount.state.requirementsSummary,
          })}
        </Text>
      )}
      {bankAccount.errorMessage && (
        <Text as="p" fontSize={descriptorTextSize} textColor="error">
          {bankAccount.errorMessage}
        </Text>
      )}
      <Flex align="center" dir="row" gap="sm" w="full">
        <RedoListDropdown
          dropdownAnchor={countryAnchor}
          dropdownOpen={countryOpen}
          items={filteredCountryOptions}
          itemSelected={({ item }) => {
            setCountryOpen(false);
            setCountrySearch("");
            bankAccount.setCountry(item.value);
          }}
          searchPlaceholder={t("Search countries")}
          searchString={countrySearch}
          selectedItems={bankAccount.country}
          setDropdownOpen={(open: boolean) => {
            setCountryOpen(open);
            if (!open) {
              setCountrySearch("");
            }
          }}
          setSearchString={setCountrySearch}
          size="xs"
        />
        <RedoButton
          dataTestId="refund-destination-connect-bank"
          flexGrow={1}
          hierarchy={bankAccount.state?.ready ? "tertiary" : "primary"}
          onClick={() => void bankAccount.connect()}
          pending={bankAccount.loading}
          size="sm"
          text={
            bankAccount.state?.ready
              ? t("Use a different bank account")
              : t("Connect bank account")
          }
        />
        <RedoButton
          dataTestId="refund-destination-bank-country"
          hierarchy="secondary"
          IconTrailing={ChevronDownIcon}
          onClick={() => setCountryOpen(!countryOpen)}
          pressed={countryOpen}
          ref={setCountryAnchor}
          size="sm"
          text={countryFlag(bankAccount.country)}
        />
      </Flex>
    </Flex>
  );

  const providerCopy: Record<
    CustomerRefundProvider,
    { icon: ReactNode; label: string; description: string }
  > = {
    [ThirdPartyRefundProvider.Paypal]: {
      icon: <PaypalIcon height={32} width={32} />,
      label: thirdPartyRefundProviderName(ThirdPartyRefundProvider.Paypal),
      description: t("{{amount}} to your PayPal account", { amount }),
    },
    [ThirdPartyRefundProvider.Venmo]: {
      icon: <VenmoIcon height={32} width={32} />,
      label: thirdPartyRefundProviderName(ThirdPartyRefundProvider.Venmo),
      description: t("{{amount}} back to your Venmo account", { amount }),
    },
    [ThirdPartyRefundProvider.Stripe]: {
      icon: <BankIcon height={32} width={32} />,
      label: t("Bank account"),
      description: t("{{amount}} to your bank account by direct deposit", {
        amount,
      }),
    },
  };

  const options: {
    id: typeof ORIGINAL_PAYMENT | CustomerRefundProvider;
    icon: ReactNode;
    isFramedIcon: boolean;
    label: string;
    description: string;
  }[] = [
    ...(allowOriginalPayment
      ? [
          {
            id: ORIGINAL_PAYMENT,
            icon: <BankIcon height={16} width={16} />,
            isFramedIcon: true,
            label: t("Original payment method"),
            description: t("{{amount}} back to your account", { amount }),
          },
        ]
      : []),
    ...providers.map((provider) => ({
      id: provider,
      isFramedIcon: false,
      ...providerCopy[provider],
    })),
  ];

  const selectedId = selectedProvider ?? ORIGINAL_PAYMENT;

  // Nothing can pay this refund: no wallet rail, and the original payment
  // method can't settle it either. Offering an empty picker would be worse
  // than saying nothing.
  if (options.length === 0) {
    return null;
  }

  return (
    <Flex data-ui="refund-method-options" flexDirection="column" gap="2xl">
      {options.map((option) => {
        const selected = option.id === selectedId;
        return (
          <PortalSelectionCard
            childrenFullWidth
            icon={option.icon}
            inputType="radio"
            isFramedIcon={option.isFramedIcon}
            key={option.id}
            onClick={() => {
              // Re-selecting the current rail would discard a pending entry.
              if (selected) {
                return;
              }
              selectProvider(option.id === ORIGINAL_PAYMENT ? null : option.id);
            }}
            selected={selected}
            title={option.label}
            uiSelector={`refund-method-${option.id}`}
          >
            <Flex dir="column" gap="none" w="full">
              <Text as="p" fontSize="sm" textColor="secondary">
                {option.description}
              </Text>
              {selected &&
                option.id !== ORIGINAL_PAYMENT &&
                (option.id === ThirdPartyRefundProvider.Stripe
                  ? bankAccountInput
                  : recipientInput)}
            </Flex>
          </PortalSelectionCard>
        );
      })}
    </Flex>
  );
});
